document.addEventListener("DOMContentLoaded", function () {
  // Template detection - works with any template containing "new"
  function getNewTemplateClass() {
    const bodyClasses = Array.from(document.body.classList);
    return bodyClasses.find(
      (className) =>
        className.includes("template-") && className.includes("new")
    );
  }

  function hasNewTemplate() {
    return !!getNewTemplateClass();
  }

  // Only proceed if we have a "new" template
  if (!hasNewTemplate()) {
    return;
  }

  const template_name = getNewTemplateClass();

  // ==========================================================================
  // CUSTOMIZATION MANAGEMENT SYSTEM
  // ==========================================================================

  // Main controller class for managing customization overlays
  class CustomizationManager {
    constructor() {
      this.addToCartManager = new AddToCartManager();
      this.initializeCustomizations();
    }

    initializeCustomizations() {
      // Dynamic initialization based on template keywords
      if (template_name.includes("steering")) {
        new SteeringWheelCustomization(this.addToCartManager);
      }

      if (template_name.includes("headlights")) {
        new PrebuiltHeadlightsCustomization(this.addToCartManager);
      }

      // Add more template types here as needed
      // if (template_name.includes('brake')) {
      //   new BrakeCustomization(this.addToCartManager);
      // }
    }
  }

  // Manages add to cart button states and events
  class AddToCartManager {
    constructor() {
      this.containers = document.querySelectorAll(
        `.${template_name} .product-info__add-button`
      );
      this.buttons = document.querySelectorAll(
        `.${template_name} .product-info__add-button button`
      );
      this.abortController = new AbortController();

      this.init();
    }

    init() {
      this.disableButtons();
      this.addClickListeners();
    }

    // Updated to handle both original and sticky buttons
    getAllButtons() {
      return document.querySelectorAll(
        `.${template_name} .product-info__add-button button, .${template_name} .sticky__content .product-info__add-button button`
      );
    }

    getAllContainers() {
      return document.querySelectorAll(
        `.${template_name} .product-info__add-button, .${template_name} .sticky__content .product-info__add-button`
      );
    }

    disableButtons() {
      this.getAllButtons().forEach((button) =>
        button.classList.add("disabled")
      );
    }

    enableButtons() {
      this.getAllButtons().forEach((button) =>
        button.classList.remove("disabled")
      );
    }

    addClickListeners() {
      this.removeClickListeners(); // Remove existing listeners first
      this.getAllContainers().forEach((container) => {
        container.addEventListener("click", this.showAlert.bind(this), {
          signal: this.abortController.signal,
        });
      });
    }

    removeClickListeners() {
      this.abortController.abort();
      this.abortController = new AbortController();
    }

    showAlert() {
      alert(
        'Please click "Start Customization" to select your options, then add to cart to check out.'
      );
    }

    destroy() {
      this.abortController.abort();
    }
  }

  // Base class for customization overlays
  class BaseCustomization {
    constructor(addToCartManager, config) {
      this.addToCartManager = addToCartManager;
      this.config = config;
      this.abortController = new AbortController();

      this.initializeElements();
      if (this.overlay) {
        this.init();
      }
    }

    initializeElements() {
      const { selectors } = this.config;
      this.overlay = document.querySelector(selectors.overlay);
      this.closeButton = document.querySelector(selectors.closeButton);
      this.nextButton = document.querySelector(selectors.nextButton);
      this.header = document.querySelector(selectors.header);
      this.closeX = document.querySelector(selectors.closeX);
      this.stepContents = selectors.stepContents.map((sel) =>
        document.querySelector(sel)
      );
      this.startButtons = document.querySelectorAll(selectors.startButtons);
    }

    init() {
      this.bindEvents();
      this.initializeStartButtons();
      this.setupStickyButtonHandler();
    }

    bindEvents() {
      const signal = this.abortController.signal;

      this.nextButton?.addEventListener("click", this.handleNext.bind(this), {
        signal,
      });
      this.closeButton?.addEventListener("click", this.handleBack.bind(this), {
        signal,
      });
      this.closeX?.addEventListener("click", this.handleClose.bind(this), {
        signal,
      });
    }

    initializeStartButtons() {
      // Handle original buttons only initially
      this.startButtons.forEach((button) => {
        setTimeout(() => {
          button.style.opacity = "1";
          button.style.pointerEvents = "inherit";
        }, 1000);

        button.addEventListener("click", this.showOverlay.bind(this), {
          signal: this.abortController.signal,
        });
      });
    }

    // Setup handler for sticky buttons that may be created later
    setupStickyButtonHandler() {
      // Use event delegation to handle dynamically created sticky buttons
      document.addEventListener(
        "click",
        (event) => {
          if (
            event.target.matches(
              `.${template_name} .sticky__content .section-customization__button`
            ) ||
            event.target.closest(
              `.${template_name} .sticky__content .section-customization__button`
            )
          ) {
            event.preventDefault();
            this.showOverlay(event);
          }
        },
        { signal: this.abortController.signal }
      );
    }

    getCurrentStepIndex() {
      return this.stepContents.findIndex(
        (div) => div && !div.classList.contains("hidden")
      );
    }

    showStep(step) {
      // Hide all steps
      this.stepContents.forEach((div, idx) => {
        if (!div) return;
        div.classList.toggle("hidden", idx !== step - 1);
      });

      // Move items to appropriate containers
      this.moveStepItems(step);

      // Update UI elements
      this.updateNavigationButtons(step);
      this.updateProgressHeader(step);
    }

    moveStepItems(step) {
      const { stepItems } = this.config;
      const stepConfig = stepItems[step];

      if (stepConfig) {
        stepConfig.forEach(({ selector, container }) => {
          this.moveItems(selector, container);
        });
      }
    }

    moveItems(selector, containerSelector) {
      const items = Array.from(document.querySelectorAll(selector));
      const container = document.querySelector(containerSelector);
      if (container) {
        items.forEach((item) => container.appendChild(item));
      }
    }

    updateNavigationButtons(step) {
      const { maxSteps } = this.config;

      this.closeButton?.classList.toggle("no_visible", step === 1);

      const span = this.nextButton?.querySelector("span");
      if (span) {
        span.textContent = step === maxSteps ? "Save" : "Next";
      }
    }

    updateProgressHeader(step) {
      if (!this.header) return;

      const lis = this.header.querySelectorAll("ul li");
      lis.forEach((li, idx) => {
        if (idx === 0) return;
        li.classList.toggle("completed", idx < step);
      });
    }

    validateCurrentStep() {
      const stepIndex = this.getCurrentStepIndex();
      const currentContainer = this.stepContents[stepIndex];

      if (!currentContainer) return true;

      let isValid = true;
      let firstInvalid = null;

      // Clear previous validation styles
      const requiredFields = currentContainer.querySelectorAll(
        "input[required], select[required], textarea[required]"
      );
      requiredFields.forEach((field) => (field.style.outline = ""));

      // Validate required fields
      requiredFields.forEach((field) => {
        const isEmpty =
          field.type === "checkbox" ? !field.checked : !field.value.trim();

        if (isEmpty) {
          field.style.outline = ".1rem solid red";
          isValid = false;
          if (!firstInvalid) firstInvalid = field;
        }
      });

      // Custom step validation
      if (!this.validateStepSpecific(stepIndex + 1, currentContainer)) {
        isValid = false;
      }

      if (!isValid && firstInvalid) {
        alert("Please complete all required fields before proceeding.");
        firstInvalid.focus();
      }

      return isValid;
    }

    validateStepSpecific(step, container) {
      // Override in subclasses for step-specific validation
      return true;
    }

    validateAllRequired() {
      let isValid = true;

      const requiredFields = this.overlay.querySelectorAll(
        "input[required], select[required], textarea[required]"
      );
      requiredFields.forEach((field) => {
        const isEmpty =
          field.type === "checkbox" ? !field.checked : !field.value.trim();
        if (isEmpty) {
          isValid = false;
          field.style.outline = ".1rem solid red";
        }
      });

      return isValid;
    }

    handleNext() {
      if (!this.validateCurrentStep()) return;

      const currentStep = this.getCurrentStepIndex() + 1;
      const { maxSteps } = this.config;

      if (currentStep === maxSteps) {
        this.overlay.classList.add("hidden");
        return;
      }

      const nextStep = Math.min(currentStep + 1, maxSteps);
      this.showStep(nextStep);
    }

    handleBack() {
      const currentStep = this.getCurrentStepIndex() + 1;
      const prevStep = Math.max(currentStep - 1, 1);
      this.showStep(prevStep);
    }

    // Fixed: X close button should not validate - user can close anytime
    handleClose(event) {
      event.preventDefault();
      this.overlay.classList.add("hidden");
    }

    showOverlay(event) {
      if (event) event.preventDefault();
      this.overlay.classList.remove("hidden");
      this.showStep(1);
    }

    destroy() {
      this.abortController.abort();
    }
  }

  // Steering wheel customization implementation
  class SteeringWheelCustomization extends BaseCustomization {
    constructor(addToCartManager) {
      const config = {
        maxSteps: 5,
        selectors: {
          overlay: ".customization__overlay",
          closeButton: "#customization-back",
          nextButton: "#customization-next",
          header: ".customization__header",
          closeX: ".customization__close",
          stepContents: [
            ".content__1",
            ".content__2",
            ".content__3",
            ".content__4",
            ".content__5",
          ],
          startButtons: `.${template_name} .section-customization__button`,
        },
        stepItems: {
          1: [
            {
              selector: ".vehicle",
              container: ".content__1 .vehicle__container",
            },
          ],
          2: [
            {
              selector: ".wheel-design",
              container: ".content__2 .wheel-design__container",
            },
          ],
          3: [
            {
              selector: ".add-ons",
              container: ".content__3 .add-ons__container",
            },
          ],
          4: [
            {
              selector: ".accessories",
              container: ".content__4 .accessories__container",
            },
          ],
          5: [
            {
              selector: ".review",
              container: ".content__5 .review__container",
            },
          ],
        },
      };

      super(addToCartManager, config);
      this.termsCheckboxHandler = null;
    }

    showStep(step) {
      super.showStep(step);

      if (step === 5) {
        this.setupTermsCheckbox();
      }
    }

    setupTermsCheckbox() {
      const termsCheckbox = document.querySelector(
        ".content__5 .review__container input[type='checkbox']"
      );

      if (termsCheckbox && this.addToCartManager) {
        // Remove previous event listener if exists
        if (this.termsCheckboxHandler) {
          termsCheckbox.removeEventListener(
            "change",
            this.termsCheckboxHandler
          );
        }

        this.termsCheckboxHandler = () => {
          if (termsCheckbox.checked) {
            this.addToCartManager.enableButtons();
            this.addToCartManager.removeClickListeners();
          } else {
            this.addToCartManager.disableButtons();
            this.addToCartManager.addClickListeners();
          }
        };

        termsCheckbox.addEventListener("change", this.termsCheckboxHandler, {
          signal: this.abortController.signal,
        });
      }
    }

    validateStepSpecific(step, container) {
      if (step === 5) {
        const termsCheckbox = container.querySelector("input[type='checkbox']");
        if (termsCheckbox && !termsCheckbox.checked) {
          termsCheckbox.style.outline = ".1rem solid red";
          alert("Please accept the terms.");
          return false;
        }
      }
      return true;
    }
  }

  // Prebuilt headlights customization implementation
  class PrebuiltHeadlightsCustomization extends BaseCustomization {
    constructor(addToCartManager) {
      const config = {
        maxSteps: 2,
        selectors: {
          overlay: `.customization__overlay.${template_name.replace(
            "template-",
            ""
          )}`,
          closeButton: `.${template_name.replace(
            "template-",
            ""
          )} #customization-back`,
          nextButton: `.${template_name.replace(
            "template-",
            ""
          )} #customization-next`,
          header: `.${template_name.replace(
            "template-",
            ""
          )} .customization__header`,
          closeX: `.${template_name.replace(
            "template-",
            ""
          )} .customization__close`,
          stepContents: [
            `.${template_name.replace("template-", "")} .content__1`,
            `.${template_name.replace("template-", "")} .content__2`,
          ],
          startButtons: `.${template_name} .section-customization__button`,
        },
        stepItems: {
          1: [
            {
              selector: ".vehicle",
              container: `.${template_name.replace(
                "template-",
                ""
              )} .content__1 .vehicle__container`,
            },
            {
              selector: ".add-ons",
              container: `.${template_name.replace(
                "template-",
                ""
              )} .content__1 .add-ons__container`,
            },
          ],
          2: [
            {
              selector: ".review",
              container: `.${template_name.replace(
                "template-",
                ""
              )} .content__2 .review__container`,
            },
          ],
        },
      };

      super(addToCartManager, config);
      this.termsCheckboxHandler = null;
    }

    showStep(step) {
      super.showStep(step);

      if (step === 2) {
        this.setupTermsCheckbox();
      }
    }

    setupTermsCheckbox() {
      const termsCheckbox = document.querySelector(
        ".review__container input[type='checkbox']"
      );

      if (termsCheckbox && this.addToCartManager) {
        // Remove previous event listener if exists
        if (this.termsCheckboxHandler) {
          termsCheckbox.removeEventListener(
            "change",
            this.termsCheckboxHandler
          );
        }

        this.termsCheckboxHandler = () => {
          if (termsCheckbox.checked) {
            this.addToCartManager.enableButtons();
            this.addToCartManager.removeClickListeners();
          } else {
            this.addToCartManager.disableButtons();
            this.addToCartManager.addClickListeners();
          }
        };

        termsCheckbox.addEventListener("change", this.termsCheckboxHandler, {
          signal: this.abortController.signal,
        });
      }
    }

    validateStepSpecific(step, container) {
      if (step === 2) {
        const termsCheckbox = container.querySelector("input[type='checkbox']");
        if (termsCheckbox && !termsCheckbox.checked) {
          termsCheckbox.style.outline = ".1rem solid red";
          alert("Please accept the terms.");
          return false;
        }
      }
      return true;
    }
  }

  // ==========================================================================
  // MOBILE STICKY FUNCTIONALITY
  // ==========================================================================

  class MobileStickyManager {
    constructor() {
      this.MOBILE_BREAKPOINT = 500;
      this.SCROLL_THRESHOLD = 150;
      this.CHAT_ADJUSTMENT_DELAY = 1000;

      this.stickyContainer = null;
      this.button = null;
      this.productForm = null;
      this.wrapper = null;
      this.isInitialized = false;
      this.lastScrollTop = 0;
      this.scrollHandler = null;
      this.resizeTimeout = null;

      this.init();
    }

    isMobile() {
      return window.innerWidth <= this.MOBILE_BREAKPOINT;
    }

    getElements() {
      return {
        stickyContainer: document.querySelector(
          `.${template_name} .sticky__container`
        ),
        button: document.querySelector(
          `.${template_name} .section-customization__button`
        ),
        productForm: document.querySelector(`.${template_name} product-form`),
      };
    }

    createStickyContent() {
      const elements = this.getElements();
      if (
        !elements.stickyContainer ||
        !elements.button ||
        !elements.productForm
      ) {
        console.warn(
          `Mobile sticky: Required elements not found for ${template_name}`
        );
        return false;
      }

      // Store references
      this.stickyContainer = elements.stickyContainer;
      this.button = elements.button;
      this.productForm = elements.productForm;

      // Create wrapper if it doesn't exist
      if (!this.wrapper) {
        this.wrapper = document.createElement("div");
        this.wrapper.classList.add("sticky__content");

        // Clone elements
        const buttonClone = this.button.cloneNode(true);
        const formClone = this.productForm.cloneNode(true);

        // Fixed: Apply the same styling to cloned start customization button
        buttonClone.style.opacity = "1";
        buttonClone.style.pointerEvents = "inherit";

        this.wrapper.appendChild(buttonClone);
        this.wrapper.appendChild(formClone);
        this.stickyContainer.appendChild(this.wrapper);

        // Trigger a custom event to notify that sticky content has been created
        document.dispatchEvent(
          new CustomEvent("stickyContentCreated", {
            detail: { templateName: template_name },
          })
        );
      }

      return true;
    }

    adjustShopifyChat(show) {
      setTimeout(() => {
        const shopifyChat = document.querySelector(
          `.${template_name} #ShopifyChat`
        );
        if (shopifyChat) {
          shopifyChat.style.bottom = show ? "8.5rem" : "";
        }
      }, this.CHAT_ADJUSTMENT_DELAY);
    }

    showStickyContainer() {
      if (this.stickyContainer) {
        this.stickyContainer.style.transform = "translateY(0)";
        this.stickyContainer.style.opacity = "1";
        this.adjustShopifyChat(true);
      }
    }

    hideStickyContainer() {
      if (this.stickyContainer) {
        this.stickyContainer.style.transform = "translateY(100%)";
        this.stickyContainer.style.opacity = "0";
        this.adjustShopifyChat(false);
      }
    }

    createScrollHandler() {
      return () => {
        const scrollTop = window.scrollY || document.documentElement.scrollTop;

        if (
          scrollTop > this.lastScrollTop &&
          scrollTop > this.SCROLL_THRESHOLD
        ) {
          // Scrolling down and passed threshold
          this.showStickyContainer();
        } else if (scrollTop < this.lastScrollTop) {
          // Scrolling up
          this.hideStickyContainer();
        }

        this.lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;
      };
    }

    initializeMobile() {
      if (this.isInitialized) return;

      if (this.createStickyContent()) {
        this.scrollHandler = this.createScrollHandler();
        window.addEventListener("scroll", this.scrollHandler, {
          passive: true,
        });
        this.isInitialized = true;
        console.log(`Mobile sticky: Initialized for ${template_name}`);
      }
    }

    cleanupMobile() {
      if (!this.isInitialized) return;

      // Remove scroll listener
      if (this.scrollHandler) {
        window.removeEventListener("scroll", this.scrollHandler);
        this.scrollHandler = null;
      }

      // Hide sticky container
      this.hideStickyContainer();

      // Remove wrapper from DOM
      if (this.wrapper && this.wrapper.parentNode) {
        this.wrapper.parentNode.removeChild(this.wrapper);
        this.wrapper = null;
      }

      // Reset state
      this.isInitialized = false;
      this.lastScrollTop = 0;
      console.log(`Mobile sticky: Cleaned up for ${template_name}`);
    }

    handleResize() {
      if (this.isMobile()) {
        this.initializeMobile();
      } else {
        this.cleanupMobile();
      }
    }

    init() {
      // Initialize based on current screen size
      this.handleResize();

      // Handle window resize with debouncing
      window.addEventListener("resize", () => {
        clearTimeout(this.resizeTimeout);
        this.resizeTimeout = setTimeout(() => this.handleResize(), 150);
      });
    }

    destroy() {
      this.cleanupMobile();
      clearTimeout(this.resizeTimeout);
    }
  }

  // ==========================================================================
  // INITIALIZE ALL SYSTEMS
  // ==========================================================================

  // Initialize the customization system (if customization blocks are present)
  let customizationManager = null;
  if (
    document.querySelector(`.${template_name} .section-customization__button`)
  ) {
    customizationManager = new CustomizationManager();
  }

  // Initialize mobile sticky functionality (if sticky elements are present)
  let mobileStickyManager = null;
  if (document.querySelector(`.${template_name} .sticky__container`)) {
    mobileStickyManager = new MobileStickyManager();
  }

  // Cleanup on page unload
  window.addEventListener("beforeunload", () => {
    if (customizationManager) {
      customizationManager.addToCartManager.destroy();
    }
    if (mobileStickyManager) {
      mobileStickyManager.destroy();
    }
  });

  console.log(`Template systems initialized for: ${template_name}`);
});
